import React, { useEffect, useState } from "react";
import "./Auth.css";
import Button from "../components/Button";
import axios from "axios";

import { API_BASE } from "../utils/apiConfig";

export default function Signup({ onAuth }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [inviteToken, setInviteToken] = useState("");
  const [invitationDetails, setInvitationDetails] = useState(null);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("invite") || "";
    if (!token) return;
    setInviteToken(token);
    axios.get(`${API_BASE}/auth/invitations/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setName(data.name || "");
        setEmail(data.email || "");
        setInvitationDetails(data);
      })
      .catch((err) => {
        setErrorCode("INVITATION_INVALID");
        setError(err?.response?.data?.error || "This invitation is invalid or has expired.");
      });
  }, []);

  const handleSignup = async () => {
    setError("");
    setErrorCode("");

    const normalizedName = name.trim();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedName || !normalizedEmail || !password) {
      setError("Please complete all fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setError("Password must be at least 8 characters and include a letter and a number.");
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(`${API_BASE}/auth/register`, {
        name: normalizedName,
        email: normalizedEmail,
        password,
        ...(inviteToken ? { inviteToken } : {})
      });

      if (response.data.accountReady) {
        onAuth("login", { email: response.data.email || normalizedEmail });
        return;
      }

      onAuth("check-email", {
        email: normalizedEmail,
        verificationEmailSent: response.data.verificationEmailSent !== false,
        notice: response.data.warning || ""
      });
    } catch (err) {
      setErrorCode(err.response?.data?.code || (err.response?.status === 409 ? "ACCOUNT_EXISTS" : ""));
      setError(
        err.response?.data?.error ||
        (err.response?.status === 409
          ? "An account with this email already exists. Please sign in or reset your password."
          : "Unable to create account. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">

        {/* LEFT PANEL */}
        <div className="auth-left">
          <h1>GET STARTED</h1>
          <h3>Create your account</h3>

          <p>
            Set up your LogicLine workspace and start analyzing
            documents with AI, securely and seamlessly.
          </p>

          <div className="bubble b1" />
          <div className="bubble b2" />
          <div className="bubble b3" />
        </div>

        {/* RIGHT PANEL */}
        <div className="auth-right">
          <h2>Sign up</h2>
          <p className="subtitle">
            {invitationDetails
              ? `${invitationDetails.role.replace("_", " ")} · ${invitationDetails.workspaceName || "LogicLine"}`
              : "Enter your details to create an account"}
          </p>

          {error && (
            <div className="auth-error" role="alert">
              <span>{error}</span>
              {errorCode === "ACCOUNT_EXISTS" && (
                <div className="auth-error-actions">
                  <button onClick={() => onAuth("login", { email: email.trim().toLowerCase() })}>Sign in</button>
                  <button onClick={() => onAuth("forgot", { email: email.trim().toLowerCase() })}>Reset password</button>
                </div>
              )}
            </div>
          )}

          <div className="input-group">
            <input
              placeholder="Full Name"
              value={name}
              disabled={Boolean(invitationDetails)}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="input-group">
            <input
              placeholder="Email"
              type="email"
              autoComplete="email"
              value={email}
              disabled={Boolean(invitationDetails)}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="input-group">
            <input
              type={show ? "text" : "password"}
              placeholder="Password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <span className="show-btn" onClick={() => setShow(!show)}>
              {show ? "HIDE" : "SHOW"}
            </span>
          </div>

          <Button
            className="primary-btn"
            onClick={handleSignup}
            loading={loading}
            disabled={!name.trim() || !email.trim() || !password}
          >
            {inviteToken ? "Accept invitation" : "Create account"}
          </Button>

          <p className="footer-text">
            Already have an account?{" "}
            <span onClick={() => onAuth("login")}>Sign in</span>
          </p>
        </div>

      </div>
    </div>
  );
}
